'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import ApplicationLogo from '@/components/ui/ApplicationLogo';
import { ApiRoutes, Routes } from '@/settings/routes';
import { HiMiniBars3, HiMiniXMark } from 'react-icons/hi2';
import { BsSearch } from 'react-icons/bs';
import Input from '../ui/Input';
import NavbarLink from './links/NavbarLink';
import { FaChevronDown, FaWhatsapp } from 'react-icons/fa6';
import axios from '@/lib/axios';
import { App_text } from '@/settings/AppText';
import { FiPhoneCall } from 'react-icons/fi';
import DropDownNavbar from '@/components/front/ui/DropDownNavbar';

const isBrowser = typeof window !== 'undefined';
const ensureProtocol = (value = '', protocol) => {
    if (!value) return '#';
    return value.startsWith(`${protocol}:`) ? value : `${protocol}:${value}`;
};

const stripContactProtocol = (value = '') => value.replace(/^(tel:|mailto:)/, '');

const getWhatsAppHref = (value = '') => {
    if (!value) return '#';
    if (value.startsWith('http')) return value;
    return `https://api.whatsapp.com/send?phone=${value}`;
};

/* ─── Tours Mega Menu (inline) ─── */
function ToursMegaMenu({ tourCategories = [], isHomePage, hasScrolled, handleCloseNavbar }) {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef(null);
    const menuRef = useRef(null);
    const hasCategories = tourCategories.length > 0;
    const textColor = isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-700';


    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
        };
        if (isOpen) document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [isOpen]);

    // Fix: reposition menu to stay inside viewport
    useEffect(() => {
        if (isOpen && menuRef.current && ref.current) {
            const triggerRect = ref.current.getBoundingClientRect();
            const menuWidth = menuRef.current.offsetWidth;
            const viewportWidth = window.innerWidth;

            // Reset first
            menuRef.current.style.left = '50%';
            menuRef.current.style.transform = 'translateX(-50%)';
            menuRef.current.style.right = 'auto';

            const menuLeft = triggerRect.left + triggerRect.width / 2 - menuWidth / 2;
            const menuRight = menuLeft + menuWidth;

            if (menuLeft < 16) {
                // Overflowing left — pin to left edge with offset
                menuRef.current.style.left = `${-triggerRect.left + 16}px`;
                menuRef.current.style.transform = 'none';
            } else if (menuRight > viewportWidth - 16) {
                // Overflowing right — pin to right edge
                menuRef.current.style.left = 'auto';
                menuRef.current.style.right = `${-(viewportWidth - triggerRect.right) + 16}px`;
                menuRef.current.style.transform = 'none';
            }
        }
    }, [isOpen]);

    if (!hasCategories) {
        return (
            <NavbarLink className={textColor} href={Routes.FRONT_tours} aria-label={App_text.generale.navbar.tours}>
                {App_text.generale.navbar.tours}
            </NavbarLink>
        );
    }

    const colCount = Math.min(tourCategories.length, 4);

    return (
        <div ref={ref} className="relative dropdown-wrapper" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
            {/* Trigger button */}
            <button
                className={`flex items-center gap-1.5 font-montserrat uppercase text-[12px] tracking-[0.05em] whitespace-nowrap font-semibold transition-colors hover:text-main-color ${textColor}`}

                onClick={() => setIsOpen((v) => !v)}
                aria-expanded={isOpen}
            >
                {App_text.generale.navbar.tours}
                <FaChevronDown size={11} className={`transition-transform duration-300 ${isOpen ? 'rotate-180' : 'rotate-0'}`} />
            </button>

            {/* Invisible bridge */}
            <div className="absolute right-0 left-0 top-full h-4" />

            {/* Mega menu panel */}
            <div
                ref={menuRef}
                className={`absolute z-[99999] bg-white rounded-xl overflow-hidden transition-all duration-300 ease-out origin-top ${
                    isOpen ? 'visible opacity-100 translate-y-0 pointer-events-auto' : 'invisible opacity-0 -translate-y-2 pointer-events-none'
                }`}
                style={{
                    top: 'calc(100% + 16px)',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: `${colCount * 240}px`,
                    maxWidth: 'min(860px, 90vw)',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08)',
                    borderTop: '3px solid #E8604C',
                }}
            >
                {/* Columns grid */}
                <div className="grid gap-x-0 px-2 py-6 divide-x divide-neutral-100" style={{ gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))` }}>
                    {tourCategories.map((parent) => (
                        <div key={parent.id} className="px-5 min-w-0">
                            <p className="mb-3 text-[10px] font-bold uppercase tracking-[0.15em] text-main-color pb-2 border-b border-main-color/20">{parent.title}</p>
                            <ul className="flex flex-col gap-0.5">
                                {(parent.children || []).map((child) => (
                                    <li key={child.id}>
                                        <Link
                                            href={`${Routes.FRONT_tours}/category/${child.slug}`}
                                            onClick={() => { setIsOpen(false); handleCloseNavbar(); }}
                                            className="group flex items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-neutral-600 transition-all duration-150 hover:bg-main-color/5 hover:text-main-color"
                                        >
                                            <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-main-color/30 transition-all group-hover:bg-main-color group-hover:scale-125" />
                                            {child.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>

                {/* Footer bar */}
                <div className="border-t border-neutral-100 bg-neutral-50/80 px-6 py-2.5 flex items-center justify-between">
                    <span className="text-[11px] text-neutral-400">Browse all Morocco tours</span>
                    <Link
                        href={Routes.FRONT_tours}
                        onClick={() => { setIsOpen(false); handleCloseNavbar(); }}
                        className="text-[12px] font-semibold text-main-color hover:underline flex items-center gap-1.5"
                    >
                        View all Morocco tours
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                        </svg>
                    </Link>
                </div>
            </div>

            {/* Backdrop click-catcher */}
            {isOpen && <div className="fixed inset-0 z-[99998]" onClick={() => setIsOpen(false)} />}
        </div>
    );
}
/* ─── Main Navbar ─── */
const Navbar = () => {
    const router = useRouter();
    const [isOpen, setIsOpen] = useState(false);
    const pathname = usePathname();
    const navRef = useRef(null);
    const [pages, setPages] = useState([]);
    const [destinations, setDestinations] = useState([]);
    const [navbarCategories, setNavbarCategories] = useState({
        events: [],
        packages: [],
        tours: [], // [{ id, title, slug, children: [{ id, title, slug }] }]
        luxury_travels: [],
    });
    const [searchKey, setSearchKey] = useState('');
    const [searchCollapse, setSearchCollapse] = useState(false);
    const [hasScrolled, setHasScrolled] = useState(false);
    const [isAnimating, setIsAnimating] = useState(false);
    const lastScrollY = useRef(0);

    const [mobileOpenKey, setMobileOpenKey] = useState(null);

    const toggleMobile = (key) => setMobileOpenKey((prev) => (prev === key ? null : key));

    const handleToggle = () => {
        setIsOpen(!isOpen);
        if (typeof document !== 'undefined') {
            if (!isOpen) document.body.classList.add('navbar-open');
            else document.body.classList.remove('navbar-open');
        }
    };

    const handleCloseNavbar = () => {
        setIsOpen(false);
        setSearchCollapse(false);
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSearchCollapse(false);
        router.push(Routes.FRONT_search + searchKey);
    };

    useEffect(() => {
        if (isBrowser) {
            import('preline/preline').then(() => {
                setTimeout(() => window.HSStaticMethods.autoInit(), 100);
            });
        }
    }, []);

    useEffect(() => {
        const controlNavbar = () => {
            const currentScrollY = window.scrollY;
            if (currentScrollY <= 100) {
                setHasScrolled(false);
                setIsAnimating(false);
            } else if (currentScrollY > 200 && !hasScrolled) {
                setIsAnimating(true);
                setHasScrolled(true);
                setTimeout(() => setIsAnimating(false), 300);
            } else {
                setHasScrolled(true);
            }
            lastScrollY.current = currentScrollY;
        };
        window.addEventListener('scroll', controlNavbar);
        return () => window.removeEventListener('scroll', controlNavbar);
    }, [hasScrolled]);

    useEffect(() => {
        axios
            .get(ApiRoutes.API_links_navbar)
            .then((res) => {
                if (res.data.status === 200) {
                    setDestinations(res.data.destinations || []);
                    setPages(res.data.pages || []);
                    if (res.data.categories) {
                        setNavbarCategories({
                            events: res.data.categories.events || [],
                            packages: res.data.categories.packages || [],
                            tours: res.data.categories.tours || [],
                            luxury_travels: res.data.categories.luxury_travels || [],
                        });
                    }
                }
            })
            .catch(console.error);
    }, []);

    useEffect(() => {
        const handleResize = () => {
            if (navRef.current) {
                if (window.innerWidth < 1320) {
                    navRef.current.style.maxHeight = isOpen ? '100vh' : '0px';
                } else {
                    navRef.current.style.maxHeight = 'none';
                }
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isOpen]);

    useEffect(() => {
        handleCloseNavbar();
    }, [pathname]);

    useEffect(() => {
        if (typeof document !== 'undefined') {
            const bottomButtons = document.querySelectorAll('.fixed-bottom, [class*="fixed"]:last-child, [class*="bottom"]');
            bottomButtons.forEach((button) => {
                if (button && !button.classList.contains('navbar-element')) {
                    button.style.display = isOpen ? 'none' : '';
                }
            });
        }
    }, [isOpen]);

    const isHomePage = pathname === '/' || pathname === '/home';

    const getHeaderClasses = () => {
        let classes = 'w-full z-50 transition-all duration-300 ease-in-out';
        if (isAnimating) classes += ' animate-slideDown';
        if (isHomePage && !hasScrolled) classes += ' bg-transparent py-4 px-2 3xl:px-0';
        else if (hasScrolled) classes += ' bg-white shadow-md backdrop-blur-sm py-4';
        else classes += ' bg-white backdrop-blur-sm py-4 border-b border-neutral-200';
        return classes;
    };

    const listColor = `${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-700'} font-semibold`;
    const phoneHref = ensureProtocol(Routes.FRONT_phone, 'tel');
    const emailHref = ensureProtocol(Routes.FRONT_email, 'mailto');
    const whatsappHref = getWhatsAppHref(Routes.FRONT_whatsapp);
    const phoneText = stripContactProtocol(Routes.FRONT_phone);
    const emailText = stripContactProtocol(Routes.FRONT_email);

    const exploreLinks = [
        { label: App_text.generale.navbar.destinations, href: Routes.FRONT_destinations },
        { label: App_text.generale.navbar.travel_guide, href: Routes.FRONT_travel_guide },
        { label: App_text.generale.navbar.blog, href: Routes.FRONT_blog },
        // { label: App_text.generale.navbar.contact, href: Routes.FRONT_contact },
    ];

    return (
        <div className="sticky top-0 z-[51] transition-transform">
            <style jsx global>{`
                @keyframes slideDown {
                    0% {
                        transform: translateY(-100%);
                    }
                    100% {
                        transform: translateY(0);
                    }
                }
                .animate-slideDown {
                    animation: slideDown 0.3s ease-in-out forwards;
                }
                body.navbar-open .fixed-bottom-button,
                body.navbar-open a[href*='TRIP'],
                body.navbar-open [class*='fixed']:last-child {
                    display: none !important;
                    opacity: 0 !important;
                    visibility: hidden !important;
                    pointer-events: none !important;
                }
                .navbar-pill-btn {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 22px;
                    border-radius: 9999px;
                    border: none;
                    background: linear-gradient(135deg, #e8604c 0%, #313041 100%);
                    color: #ffffff !important;
                    font-size: 0.875rem;
                    font-weight: 600;
                    text-decoration: none;
                    box-shadow: 0 4px 14px rgba(232, 96, 76, 0.4);
                    white-space: nowrap;
                    cursor: pointer;
                    transition:
                        box-shadow 0.25s ease,
                        transform 0.2s ease;
                }
                .navbar-pill-btn:hover {
                    box-shadow: 0 6px 22px rgba(232, 96, 76, 0.55);
                    transform: translateY(-1px);
                    color: #ffffff !important;
                }
                .navbar-pill-btn-mobile {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 8px;
                    width: 100%;
                    padding: 14px 28px;
                    border-radius: 9999px;
                    border: none;
                    background: linear-gradient(135deg, #e8604c 0%, #313041 100%);
                    color: #ffffff !important;
                    font-size: 1rem;
                    font-weight: 600;
                    text-decoration: none;
                    box-shadow: 0 4px 14px rgba(232, 96, 76, 0.4);
                    cursor: pointer;
                    transition:
                        box-shadow 0.25s ease,
                        transform 0.2s ease;
                    margin-top: 8px;
                    margin-bottom: 8px;
                }
                .navbar-pill-btn-mobile:hover {
                    box-shadow: 0 6px 22px rgba(232, 96, 76, 0.55);
                    transform: translateY(-1px);
                    color: #ffffff !important;
                }
                .navbar-desktop-icon {
                    display: flex;
                }
                .navbar-desktop-shell {
                    display: none !important;
                }
                .navbar-mobile-shell {
                    display: flex !important;
                }
                .navbar-mobile-menu-shell {
                    display: block !important;
                }
                @media (min-width: 1320px) {
                    .navbar-desktop-shell {
                        display: flex !important;
                    }
                    .navbar-mobile-shell,
                    .navbar-mobile-menu-shell {
                        display: none !important;
                    }
                }
                @media (max-width: 1679px) {
                    .navbar-desktop-icon {
                        display: none !important;
                    }
                }
                .navbar-mobile-tours-grid {
                    display: flex;
                    flex-direction: column;
                }
                @media (min-width: 920px) and (max-width: 1319px) {
                    .navbar-mobile-tours-grid {
                        display: grid;
                        grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
                        gap: 1rem;
                        align-items: start;
                    }
                    .navbar-mobile-tours-all-link {
                        grid-column: 1 / -1;
                    }
                }
            `}</style>

            <header className={getHeaderClasses()} style={{ position: 'relative', zIndex: 99999 }}>
                <div className={`relative w-full px-4 mx-auto xl:px-6 rounded-lg backdrop-blur-sm ${isHomePage && !hasScrolled ? 'bg-white/40 lg:py-2' : 'bg-white'}`}>
                    {/* ── Desktop Navbar ── */}
                    <nav className="hidden items-center w-full lg:flex navbar-desktop-shell">
                        {/* Logo */}
                        <div className="flex flex-1 items-center">
                            <Link prefetch={false} href={Routes.HOME} className="inline-block text-xl font-semibold rounded-xl focus:outline-hidden focus:opacity-80" aria-label={App_text.APP_NAME}>
                                <ApplicationLogo />
                            </Link>
                        </div>

                        {/* Nav Links */}
                        <div className="flex flex-none gap-x-4 justify-center items-center whitespace-nowrap">
                            <NavbarLink className={listColor} href={Routes.HOME} aria-label={App_text.generale.navbar.home}>
                                {App_text.generale.navbar.home}
                            </NavbarLink>

                            {pages.length !== 0 &&
                                pages.map((page, index) => (
                                    <NavbarLink className={listColor} key={index} href={`/${page.slug ?? '#'}`} aria-label={page.name}>
                                        {page.name}
                                    </NavbarLink>
                                ))}

                            {/* 1. Tours Mega Menu */}
                            <ToursMegaMenu tourCategories={navbarCategories.tours} isHomePage={isHomePage} hasScrolled={hasScrolled} handleCloseNavbar={handleCloseNavbar} />

                            {/* 2. Packages */}
                            {navbarCategories.packages.length > 0 ? (
                                <DropDownNavbar label={App_text.generale.navbar.packages} href={Routes.FRONT_packages} handleCloseNavbar={handleCloseNavbar} linkClassName={listColor}>
                                    <div className="flex flex-col gap-1">
                                        {navbarCategories.packages.map((cat) => (
                                            <NavbarLink
                                                key={`pkg-cat-${cat.id}`}
                                                href={cat.slug ? Routes.FRONT_packages_category + cat.slug : '#'}
                                                variant="dropdown"
                                                className="px-4 py-2 text-sm text-neutral-700"
                                            >
                                                {cat.title}
                                            </NavbarLink>
                                        ))}
                                        <div className="pt-1 mt-1 border-t border-neutral-100">
                                            <NavbarLink
                                                href={Routes.FRONT_packages}
                                                onClick={handleCloseNavbar}
                                                variant="dropdown"
                                                className="flex gap-1 items-center px-4 py-2 text-sm font-semibold text-main-color"
                                            >
                                                View all packages
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </NavbarLink>
                                        </div>
                                    </div>
                                </DropDownNavbar>
                            ) : (
                                <NavbarLink className={listColor} href={Routes.FRONT_packages} aria-label={App_text.generale.navbar.packages}>
                                    {App_text.generale.navbar.packages}
                                </NavbarLink>
                            )}

                            {/* 3. Events */}
                            {navbarCategories.events.length > 0 ? (
                                <DropDownNavbar label={App_text.generale.navbar.events} href={Routes.FRONT_events} handleCloseNavbar={handleCloseNavbar} linkClassName={listColor}>
                                    <div className="flex flex-col gap-1">
                                        {navbarCategories.events.map((cat) => (
                                            <NavbarLink
                                                key={`ev-cat-${cat.id}`}
                                                href={cat.slug ? Routes.FRONT_events_category + cat.slug : '#'}
                                                variant="dropdown"
                                                className="px-4 py-2 text-sm text-neutral-700"
                                            >
                                                {cat.title}
                                            </NavbarLink>
                                        ))}
                                        <div className="pt-1 mt-1 border-t border-neutral-100">
                                            <NavbarLink
                                                href={Routes.FRONT_events}
                                                onClick={handleCloseNavbar}
                                                variant="dropdown"
                                                className="flex gap-1 items-center px-4 py-2 text-sm font-semibold text-main-color"
                                            >
                                                View all events
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </NavbarLink>
                                        </div>
                                    </div>
                                </DropDownNavbar>
                            ) : (
                                <NavbarLink className={listColor} href={Routes.FRONT_events} aria-label={App_text.generale.navbar.events}>
                                    {App_text.generale.navbar.events}
                                </NavbarLink>
                            )}

                            {/* 4. Exclusive Experiences */}
                            {navbarCategories.luxury_travels.length > 0 ? (
                                <DropDownNavbar label={App_text.generale.navbar.luxury_travels} href={Routes.FRONT_luxury_travels} handleCloseNavbar={handleCloseNavbar} linkClassName={listColor}>
                                    <div className="flex flex-col gap-1">
                                        {navbarCategories.luxury_travels.map((cat) => (
                                            <NavbarLink
                                                key={`lux-cat-${cat.id}`}
                                                href={cat.slug ? Routes.FRONT_luxury_travel_category + cat.slug : '#'}
                                                variant="dropdown"
                                                className="px-4 py-2 text-sm text-neutral-700"
                                            >
                                                {cat.title}
                                            </NavbarLink>
                                        ))}
                                        <div className="pt-1 mt-1 border-t border-neutral-100">
                                            <NavbarLink
                                                href={Routes.FRONT_luxury_travels}
                                                onClick={handleCloseNavbar}
                                                variant="dropdown"
                                                className="flex gap-1 items-center px-4 py-2 text-sm font-semibold text-main-color"
                                            >
                                                View all Experiences
                                                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="5" y1="12" x2="19" y2="12" />
                                                    <polyline points="12 5 19 12 12 19" />
                                                </svg>
                                            </NavbarLink>
                                        </div>
                                    </div>
                                </DropDownNavbar>
                            ) : (
                                <NavbarLink className={listColor} href={Routes.FRONT_luxury_travels} aria-label={App_text.generale.navbar.luxury_travels}>
                                    {App_text.generale.navbar.luxury_travels}
                                </NavbarLink>
                            )}

                            {/* 5. Explore */}
                            <DropDownNavbar label={App_text.generale.navbar.explore} handleCloseNavbar={handleCloseNavbar} linkClassName={listColor}>
                                <div className="flex flex-col gap-1">
                                    {exploreLinks.map((link, index) => (
                                        <NavbarLink key={`explore-link-${index}`} href={link.href ?? '#'} variant="dropdown" className="px-4 py-2 text-sm text-neutral-700">
                                            {link.label}
                                        </NavbarLink>
                                    ))}
                                </div>
                            </DropDownNavbar>
                        </div>

                        {/* Right: icons + CTA */}
                        <div className="flex flex-1 gap-x-4 justify-end items-center">
                            <button
                                type="button"
                                onClick={() => setSearchCollapse(!searchCollapse)}
                                aria-label="Search"
                                className="justify-center items-center w-9 h-9 rounded-full transition-all duration-300 navbar-desktop-icon"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <BsSearch className="size-4" />
                            </button>
                            <Link
                                prefetch={false}
                                href={phoneHref}
                                aria-label="Call us"
                                className="justify-center items-center w-9 h-9 rounded-full transition-all duration-300 navbar-desktop-icon"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <FiPhoneCall className="size-4" />
                            </Link>
                            <Link
                                prefetch={false}
                                href={whatsappHref}
                                target="_blank"
                                aria-label="WhatsApp"
                                className="justify-center items-center w-9 h-9 rounded-full transition-all duration-300 navbar-desktop-icon"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <FaWhatsapp className="size-4" />
                            </Link>
                            <Link prefetch={false} href={Routes.FRONT_tailor_made} className="uppercase navbar-pill-btn" aria-label="Plan Your Trip">
                                <span>{App_text.generale.navbar.button_action}</span>
                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </Link>
                        </div>
                    </nav>

                    {/* ── Mobile Top Bar ── */}
                    <div className="flex justify-between items-center py-2 lg:hidden navbar-mobile-shell" style={{ position: 'relative', zIndex: 99999 }}>
                        <Link
                            prefetch={false}
                            href={Routes.HOME}
                            className="inline-block flex-none text-xl font-semibold rounded-xl focus:outline-hidden focus:opacity-80"
                            aria-label={App_text.APP_NAME}
                        >
                            <ApplicationLogo />
                        </Link>
                        <div className="flex gap-x-2 items-center">
                            <button
                                type="button"
                                onClick={() => setSearchCollapse(!searchCollapse)}
                                aria-label="Search"
                                className="flex justify-center items-center w-9 h-9 rounded-full transition-all duration-300"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <BsSearch className="size-4" />
                            </button>
                            <Link
                                prefetch={false}
                                href={phoneHref}
                                aria-label="Call us"
                                className="flex justify-center items-center w-9 h-9 rounded-full transition-all duration-300"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <FiPhoneCall className="size-4" />
                            </Link>
                            <Link
                                prefetch={false}
                                href={whatsappHref}
                                target="_blank"
                                aria-label="WhatsApp"
                                className="flex justify-center items-center w-9 h-9 rounded-full transition-all duration-300"
                                style={{ background: isHomePage && !hasScrolled ? 'rgba(255,255,255,0.15)' : 'rgba(49,48,65,0.06)', color: isHomePage && !hasScrolled ? '#ffffff' : '#313041' }}
                            >
                                <FaWhatsapp className="size-4" />
                            </Link>
                            <button
                                onClick={handleToggle}
                                type="button"
                                className={`flex justify-center items-center text-lg font-semibold rounded-lg border border-gray-200 ${listColor} size-9 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none hover:text-main-color`}
                                aria-expanded={isOpen}
                                aria-label="Toggle Navigation"
                            >
                                {isOpen ? <HiMiniXMark className="size-4" /> : <HiMiniBars3 className="size-4" />}
                            </button>
                        </div>
                    </div>

                    {/* ── Mobile Menu ── */}
                    <div
                        ref={navRef}
                        style={{
                            position: 'fixed',
                            top: '72px',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            width: '100%',
                            height: isOpen ? 'calc(100vh - 72px)' : '0',
                            backgroundColor: isHomePage && !hasScrolled ? 'rgb(49, 48, 65)' : 'white',
                            borderRadius: isHomePage && !hasScrolled ? '10px' : 'unset',
                            backdropFilter: 'blur(10px)',
                            WebkitBackdropFilter: 'blur(10px)',
                            zIndex: 99998,
                            overflow: 'hidden',
                            transition: 'height 0.3s ease-in-out',
                        }}
                        className="lg:hidden navbar-mobile-menu-shell"
                    >
                        <div className="flex overflow-y-auto flex-col gap-y-4 px-6 py-8 w-full h-full max-w-8xl">
                            <NavbarLink className={`text-base mb-2 font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`} onClick={handleCloseNavbar} href={Routes.HOME}>
                                {App_text.generale.navbar.home}
                            </NavbarLink>

                            {pages.length !== 0 &&
                                pages.map((page, index) => (
                                    <NavbarLink
                                        key={index}
                                        className={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                        href={`/${page.slug}`}
                                        onClick={handleCloseNavbar}
                                    >
                                        {page.name}
                                    </NavbarLink>
                                ))}

                            {/* ── Mobile Tours — grouped by parent ── */}
                            {navbarCategories.tours.length > 0 ? (
                                <DropDownNavbar
                                    label={App_text.generale.navbar.tours}
                                    href={Routes.FRONT_tours}
                                    handleCloseNavbar={handleCloseNavbar}
                                    linkClassName={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                    isOpen={mobileOpenKey === 'tours'}
                                    onToggle={() => toggleMobile('tours')}
                                >
                                    <div className="py-2 navbar-mobile-tours-grid">
                                        {navbarCategories.tours.map((parent) => (
                                            <div key={`m-tour-parent-${parent.id}`} className="mb-3">
                                                {/* Parent label — not a link */}
                                                <p
                                                    className={`px-2 pt-1 pb-1.5 text-[12px] font-bold uppercase tracking-widest ${isHomePage && !hasScrolled ? 'text-main-color/80' : 'text-main-color'}`}
                                                >
                                                    {parent.title}
                                                </p>
                                                {/* Children links */}
                                                <div className="flex flex-col gap-0.5 pl-2 border-l-2 border-main-color/20 ml-2">
                                                    {(parent.children || []).map((child) => (
                                                        <NavbarLink
                                                            key={`m-tour-child-${child.id}`}
                                                            href={`${Routes.FRONT_tours}/category/${child.slug}`}
                                                            onClick={handleCloseNavbar}
                                                            variant="dropdown"
                                                            className={`text-[14px] py-1 ${isHomePage && !hasScrolled ? 'text-white/80' : 'text-neutral-600'}`}
                                                        >
                                                            {child.title}
                                                        </NavbarLink>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                        {/* View all link */}
                                        <NavbarLink
                                            href={Routes.FRONT_tours}
                                            onClick={handleCloseNavbar}
                                            variant="dropdown"
                                            className="navbar-mobile-tours-all-link mt-1 text-[14px] font-semibold text-main-color"
                                        >
                                            View all tours →
                                        </NavbarLink>
                                    </div>
                                </DropDownNavbar>
                            ) : (
                                <NavbarLink
                                    className={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                    onClick={handleCloseNavbar}
                                    href={Routes.FRONT_tours}
                                >
                                    {App_text.generale.navbar.tours}
                                </NavbarLink>
                            )}

                            {/* Mobile Packages */}
                            <DropDownNavbar
                                label={App_text.generale.navbar.packages}
                                href={Routes.FRONT_packages}
                                handleCloseNavbar={handleCloseNavbar}
                                linkClassName={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                isOpen={mobileOpenKey === 'packages'}
                                onToggle={() => toggleMobile('packages')}
                            >
                                <div className="flex flex-col gap-0.5 py-1">
                                    {navbarCategories.packages.map((cat) => (
                                        <NavbarLink
                                            key={`m-pkg-cat-${cat.id}`}
                                            href={cat.slug ? Routes.FRONT_packages_category + cat.slug : '#'}
                                            onClick={handleCloseNavbar}
                                            variant="dropdown"
                                            className={`text-[14px] py-1 ${isHomePage && !hasScrolled ? 'text-white/80' : 'text-neutral-600'}`}
                                        >
                                            {cat.title}
                                        </NavbarLink>
                                    ))}
                                    <NavbarLink href={Routes.FRONT_packages} onClick={handleCloseNavbar} variant="dropdown" className="mt-1 text-[14px] font-semibold text-main-color">
                                        View all packages →
                                    </NavbarLink>
                                </div>
                            </DropDownNavbar>

                            {/* Mobile Events */}
                            <DropDownNavbar
                                label={App_text.generale.navbar.events}
                                href={Routes.FRONT_events}
                                handleCloseNavbar={handleCloseNavbar}
                                linkClassName={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                isOpen={mobileOpenKey === 'events'}
                                onToggle={() => toggleMobile('events')}
                            >
                                <div className="flex flex-col gap-0.5 py-1">
                                    {navbarCategories.events.map((cat) => (
                                        <NavbarLink
                                            key={`m-ev-cat-${cat.id}`}
                                            href={cat.slug ? Routes.FRONT_events_category + cat.slug : '#'}
                                            onClick={handleCloseNavbar}
                                            variant="dropdown"
                                            className={`text-[14px] py-1 ${isHomePage && !hasScrolled ? 'text-white/80' : 'text-neutral-600'}`}
                                        >
                                            {cat.title}
                                        </NavbarLink>
                                    ))}
                                    <NavbarLink href={Routes.FRONT_events} onClick={handleCloseNavbar} variant="dropdown" className="mt-1 text-[14px] font-semibold text-main-color">
                                        View all events →
                                    </NavbarLink>
                                </div>
                            </DropDownNavbar>

                            {/* Mobile Exclusive Experiences */}
                            <DropDownNavbar
                                label={App_text.generale.navbar.luxury_travels}
                                href={Routes.FRONT_luxury_travels}
                                handleCloseNavbar={handleCloseNavbar}
                                linkClassName={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                isOpen={mobileOpenKey === 'luxury_travels'}
                                onToggle={() => toggleMobile('luxury_travels')}
                            >
                                <div className="flex flex-col gap-0.5 py-1">
                                    {navbarCategories.luxury_travels.map((cat) => (
                                        <NavbarLink
                                            key={`m-lux-cat-${cat.id}`}
                                            href={cat.slug ? Routes.FRONT_luxury_travel_category + cat.slug : '#'}
                                            onClick={handleCloseNavbar}
                                            variant="dropdown"
                                            className={`text-[14px] py-1 ${isHomePage && !hasScrolled ? 'text-white/80' : 'text-neutral-600'}`}
                                        >
                                            {cat.title}
                                        </NavbarLink>
                                    ))}
                                    <NavbarLink href={Routes.FRONT_luxury_travels} onClick={handleCloseNavbar} variant="dropdown" className="mt-1 text-[14px] font-semibold text-main-color">
                                        View all Experiences →
                                    </NavbarLink>
                                </div>
                            </DropDownNavbar>

                            {/* Mobile Explore */}
                            <DropDownNavbar
                                label={App_text.generale.navbar.explore}
                                handleCloseNavbar={handleCloseNavbar}
                                linkClassName={`text-base font-semibold ${isHomePage && !hasScrolled ? 'text-white' : 'text-neutral-800'}`}
                                isOpen={mobileOpenKey === 'explore'}
                                onToggle={() => toggleMobile('explore')}
                            >
                                <div className="flex flex-col gap-0.5 py-1">
                                    {exploreLinks.map((link, index) => (
                                        <NavbarLink
                                            key={`m-explore-link-${index}`}
                                            href={link.href ?? '#'}
                                            onClick={handleCloseNavbar}
                                            variant="dropdown"
                                            className={`text-[14px] py-1 ${isHomePage && !hasScrolled ? 'text-white/80' : 'text-neutral-600'}`}
                                        >
                                            {link.label}
                                        </NavbarLink>
                                    ))}
                                </div>
                            </DropDownNavbar>

                            {/* Social row */}
                            <div className={`flex items-center gap-4 pt-2 pb-1 border-t ${isHomePage && !hasScrolled ? 'border-white/20' : 'border-gray-200'}`}>
                                <Link
                                    prefetch={false}
                                    href={whatsappHref}
                                    target="_blank"
                                    aria-label="WhatsApp"
                                    className="flex justify-center items-center w-11 h-11 rounded-full transition-all duration-300"
                                    style={{ background: 'linear-gradient(135deg, #E8604C 0%, #313041 100%)', boxShadow: '0 4px 12px rgba(232,96,76,0.35)' }}
                                >
                                    <FaWhatsapp className="text-white size-5" />
                                </Link>
                                <Link
                                    prefetch={false}
                                    href={phoneHref}
                                    aria-label="Call us"
                                    className="flex justify-center items-center w-11 h-11 rounded-full transition-all duration-300"
                                    style={{ background: 'linear-gradient(135deg, #313041 0%, #E8604C 100%)', boxShadow: '0 4px 12px rgba(49,48,65,0.35)' }}
                                >
                                    <FiPhoneCall className="text-white size-5" />
                                </Link>
                                <div className={`flex flex-col ml-2 ${isHomePage && !hasScrolled ? 'text-white/70' : 'text-gray-500'}`}>
                                    <span className="text-xs font-medium">{phoneText}</span>
                                    <span className="text-xs">{emailText}</span>
                                </div>
                            </div>

                            {/* Mobile CTA */}
                            <Link prefetch={false} onClick={handleCloseNavbar} href={Routes.FRONT_tailor_made} className="navbar-pill-btn-mobile" aria-label={App_text.generale.navbar.button_action}>
                                <span>{App_text.generale.navbar.button_action}</span>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <line x1="5" y1="12" x2="19" y2="12" />
                                    <polyline points="12 5 19 12 12 19" />
                                </svg>
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            {/* Search Form */}
            <div className={`transition-all duration-300 ${searchCollapse ? 'max-h-96' : 'max-h-0'} overflow-hidden`}>
                <form onSubmit={handleSearch} className="flex items-center w-full">
                    <Input type="text" onChange={(e) => setSearchKey(e.target.value)} placeholder="Search for..." className="w-full h-16" required />
                    <button type="submit" className="px-4 h-16 text-white transition-colors w-fit md:w-48 bg-secound-color hover:opacity-80">
                        {App_text.generale.navbar.button_search}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Navbar;
